/**
 * Main CivilGrid web app Pylon Chat Widget — single source of truth for App ID.
 * Currently unique to packages/client. DRF has a seperate instance of the widget.
 * @see https://docs.usepylon.com/pylon-docs/chat-widget/chat-setup
 */
export const PYLON_CLIENT_APP_ID = 'ededbffd-7408-48e7-9283-87fdd1b8c9b3';

/**
 * Skip Pylon on headless routes — those are loaded by Puppeteer to capture map
 * screenshots for PDF exports, and the chat bubble would otherwise be baked into
 * the screenshot (MRF-172).
 *
 * Uses the same case-insensitive substring check as other client-side headless
 * gates (see `App2.tsx` tracking init, `Disclaimer.tsx`, `Map.tsx` preserveDrawingBuffer).
 *
 * @param pathname Pass `window.location.pathname`.
 */
export function shouldBootstrapPylon(pathname: string): boolean {
  return !pathname.toLowerCase().includes('headless');
}

/**
 * Pylon’s install snippet: queue `window.Pylon` before the widget script loads, then inject the async script.
 */
export function bootstrapPylonClientWidget(): void {
  const w = window;
  const d = document;

  type PylonStub = (() => void) & { q: IArguments[]; e: (args: IArguments) => void };

  const pylon = function () {
    // biome-ignore lint/complexity/noArguments: must match Pylon’s install snippet (queues `arguments` objects)
    pylon.e(arguments);
  } as PylonStub;

  pylon.q = [];
  pylon.e = (args: IArguments) => {
    pylon.q.push(args);
  };
  w.Pylon = pylon as Window['Pylon'];

  const load = () => {
    const s = d.createElement('script');
    s.setAttribute('type', 'text/javascript');
    s.setAttribute('async', 'true');
    s.setAttribute('src', `https://widget.usepylon.com/widget/${PYLON_CLIENT_APP_ID}`);
    const first = d.getElementsByTagName('script')[0];
    first?.parentNode?.insertBefore(s, first);
  };

  if (d.readyState === 'complete') {
    load();
  } else {
    w.addEventListener('load', load, false);
  }
}

/** Pylon renders its chat window as a same-origin iframe with this id in the host document. */
export const PYLON_CHAT_FRAME_ID = 'pylon-chat-window';
const PYLON_FOCUS_STYLE_ID = 'civilgrid-pylon-focus-visible';
const PYLON_FOCUSABLE = 'textarea, input:not([type="hidden"]), button, [href], select, [tabindex]:not([tabindex="-1"])';
const PYLON_FOCUS_POLL_MS = 50;
const PYLON_FOCUS_TIMEOUT_MS = 2000;

/**
 * Opens the Pylon chat and moves keyboard focus into it; when the chat closes, focus returns to `returnFocusTo`.
 *
 * Pylon's `onShow` / `onHide` hold a single callback each, so calling this replaces any previously registered ones.
 * Relies on Pylon's `#pylon-chat-window` iframe; if that id changes, the chat still opens and focus stays put.
 *
 * @param returnFocusTo Element to refocus when the chat closes, typically the control that opened it.
 * @returns Whether the chat was asked to open (`false` when the widget isn't bootstrapped).
 */
export function openPylonChatWithFocus(returnFocusTo: HTMLElement): boolean {
  const pylon = window.Pylon;
  if (!pylon) return false;

  pylon('onShow', () => {
    void focusPylonChatWhenMounted();
  });
  pylon('onHide', () => {
    const active = document.activeElement;
    const focusIsInChatOrLost =
      !active || active === document.body || active === document.getElementById(PYLON_CHAT_FRAME_ID);
    if (focusIsInChatOrLost && returnFocusTo.isConnected) returnFocusTo.focus();
  });
  pylon('show');
  return true;
}

/**
 * Polls until the Pylon chat iframe has mounted and something inside it accepts focus.
 *
 * @returns Whether focus landed inside the chat; on timeout, focuses the iframe itself if it exists.
 */
export function focusPylonChatWhenMounted(timeoutMs = PYLON_FOCUS_TIMEOUT_MS): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  return new Promise((resolve) => {
    const attempt = () => {
      if (focusInsidePylonChat()) {
        resolve(true);
        return;
      }
      if (Date.now() >= deadline) {
        document.getElementById(PYLON_CHAT_FRAME_ID)?.focus();
        resolve(false);
        return;
      }
      setTimeout(attempt, PYLON_FOCUS_POLL_MS);
    };
    attempt();
  });
}

function focusInsidePylonChat(): boolean {
  const frame = document.getElementById(PYLON_CHAT_FRAME_ID);
  if (!(frame instanceof HTMLIFrameElement)) return false;
  const doc = frame.contentDocument;
  if (!doc) return false;

  const target = doc.querySelector<HTMLElement>('textarea') ?? doc.querySelector<HTMLElement>(PYLON_FOCUSABLE);
  if (!target) return false;

  ensureFocusVisibleStyle(doc);
  target.focus();
  // A still-hidden chat window rejects focus, so confirm it landed before stopping the poll.
  return doc.activeElement === target;
}

/** Guarantees a visible focus ring inside the chat (WCAG 2.4.7) regardless of Pylon's own styles. */
function ensureFocusVisibleStyle(doc: Document): void {
  if (doc.getElementById(PYLON_FOCUS_STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = PYLON_FOCUS_STYLE_ID;
  style.textContent = ':focus-visible { outline: 2px solid #196fe0 !important; outline-offset: 2px !important; }';
  doc.head.appendChild(style);
}
